import { Region, SagaUnitSystem, useSagaAuth, useSagaSettings } from "@einride/saga-core"
import { getBrowserLanguage } from "lib/language"
import React, { createContext, useContext, useEffect, useMemo, useState } from "react"

export enum UnitSystem {
  Metric = "metric",
  Imperial = "imperial",
}

type UserPreferencesContextState = {
  unitSystem: UnitSystem
  locale: string
}

export const UserPreferencesContext = createContext<UserPreferencesContextState | undefined>(
  undefined,
)

export const UserPreferencesProvider = ({
  children,
}: React.PropsWithChildren<unknown>): React.JSX.Element => {
  const { settings } = useSagaSettings()
  const { activeOrganization } = useSagaAuth()
  const [unitSystem, setUnitSystem] = useState(UnitSystem.Metric)
  const [locale, setLocale] = useState(getBrowserLanguage)

  useEffect(() => {
    setUnitSystem(getUnitSystem(settings.unitSystem, activeOrganization.region))
    setLocale(getLocaleBasedOnRegion(activeOrganization.region))
  }, [activeOrganization.region, settings.unitSystem])

  const value = useMemo(() => {
    return {
      unitSystem,
      locale,
    }
  }, [unitSystem, locale])

  return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
}

// These two functions are copied from saga-core.
const getUnitSystem = (value: SagaUnitSystem, region: Region): UnitSystem => {
  if (value === "followRegion") {
    return getRegionalUnitSystem(region)
  }
  return value === "US" ? UnitSystem.Imperial : UnitSystem.Metric
}

const getRegionalUnitSystem = (region: Region): UnitSystem => {
  return region === "US" ? UnitSystem.Imperial : UnitSystem.Metric
}

// Sweden and Germany have the same date format
export function getLocaleBasedOnRegion(region: Region): string {
  return region === "US" ? "en-US" : "en-SE"
}

export const useUserPreferences = (): UserPreferencesContextState => {
  const userPreferenceContext = useContext(UserPreferencesContext)
  if (!userPreferenceContext) {
    throw new Error("useUserPreferences without UserPreferencesContext")
  }

  return userPreferenceContext
}
